import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { PresenceState } from '@/contexts/PresenceContext'

/**
 * One dot, one meaning, everywhere presence is shown.
 *
 * Defined once so a status cannot be green in one panel and grey in another. Adding a status is
 * an entry in each of these two maps and nothing else — the presence layer already carries any
 * value the server sends.
 *
 * Away is amber rather than grey: grey is what offline looks like, and an away colleague is still
 * reachable — you would leave them a message where you would not ring them.
 */
const TONE: Record<PresenceState, string> = {
  available: 'bg-green-500',
  busy: 'bg-red-500',
  away: 'bg-amber-500',
  offline: 'bg-gray-300 dark:bg-gray-600',
}

const LABEL: Record<PresenceState, { key: string; fallback: string }> = {
  available: { key: 'presence.available', fallback: 'Available' },
  busy: { key: 'presence.busy', fallback: 'Busy' },
  away: { key: 'presence.away', fallback: 'Away' },
  offline: { key: 'presence.offline', fallback: 'Offline' },
}

export function presenceLabelKey(status: PresenceState) {
  return LABEL[status] ?? LABEL.offline
}

export function PresenceDot({ status, className }: { status: PresenceState; className?: string }) {
  const { t } = useTranslation()
  const label = presenceLabelKey(status)
  return (
    <span
      // A colour alone is not a status for anyone who cannot distinguish these colours, and this
      // dot is the only thing carrying the information in most places it appears.
      role="img"
      aria-label={t(label.key, label.fallback)}
      title={t(label.key, label.fallback)}
      className={cn('rounded-full border-2 border-background', TONE[status] ?? TONE.offline, className)}
    />
  )
}
